/** Haversine distance (BR-4). Mirrors backend `apps/core/utils/geo.py`. */

export interface LatLng {
  lat: number
  lng: number
}

/** Pandikkad bus stand — last-resort reference point (BR-4). */
export const DEFAULT_REFERENCE: LatLng = { lat: 11.0985, lng: 76.2322 }

const R = 6371
const rad = (d: number) => (d * Math.PI) / 180

export function haversineKm(a: LatLng, b: LatLng): number {
  const dLat = rad(b.lat - a.lat)
  const dLng = rad(b.lng - a.lng)
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(h))
}

/**
 * Reference point for distances and nearest-first (BR-4): the user's location if they shared it
 * (D-05) → else the selected town's centre → else Pandikkad bus stand.
 */
export function referencePoint(
  user: LatLng | null,
  town: { latitude: number | null; longitude: number | null } | null | undefined,
): LatLng {
  if (user) return user
  if (town && town.latitude != null && town.longitude != null) {
    return { lat: town.latitude, lng: town.longitude }
  }
  return DEFAULT_REFERENCE
}

/** km from `ref` to a shop's coordinates, or null when the shop has none. */
export function distanceKm(
  ref: LatLng,
  shop: { latitude: number | null; longitude: number | null },
): number | null {
  if (shop.latitude == null || shop.longitude == null) return null
  return haversineKm(ref, { lat: shop.latitude, lng: shop.longitude })
}

/** Nearest first (BR-4); shops without coordinates last, otherwise stable. */
export function sortByDistance<T extends { latitude: number | null; longitude: number | null }>(
  items: readonly T[],
  ref: LatLng,
): T[] {
  const d = (s: T) => distanceKm(ref, s) ?? Number.POSITIVE_INFINITY
  return [...items].sort((a, b) => d(a) - d(b))
}

/** A town is "near" when its centre is within this many km of the shopper (BR-24). */
export const NEAR_TOWN_KM = 15

/** The town whose centre is closest to `at`, if one is within `maxKm` (BR-24). */
export function nearestTown<T extends { latitude: number | null; longitude: number | null }>(
  at: LatLng,
  towns: readonly T[],
  maxKm: number = NEAR_TOWN_KM,
): T | null {
  let best: T | null = null
  let bestKm = maxKm
  for (const t of towns) {
    const d = distanceKm(at, t)
    if (d != null && d <= bestKm) {
      best = t
      bestKm = d
    }
  }
  return best
}
